import { formatChapterTitle } from '../../../shared/utils/chapterTitle';

// Public /series directory reads for SSG prerender + the splash/show/episode loaders. Everything
// comes from ONE static JSON artifact on the Files CDN (services/routes/tts/directory.ts writes
// it on every public-tier privacy flip) — no API/Lambda/DB/WS at request time. The whole file
// is fetched once and sliced in memory, so prerender (Node) and a future client nav share one
// fetch. Node-prerender-safe: fetch only, no store/Amplify/@shared imports (see routes.ts).
// Files CDN base, per-stage (infra/gnothi/web.ts threads `routerFiles.url`): the old hardcoded prod host has no DNS record on staging, so the prerender's fetch died and took the whole build with it.
const FILES = import.meta.env.VITE_FILES_URL || 'https://files.gnothiai.com';
// LISTED_SHOWS_KEY, verbatim from services/routes/tts/directory.ts (C1).
export const LISTED_SHOWS_URL = `${FILES}/_system/system/listed_shows/listed-shows.json`;

export interface FeaturedShow {
  id: string; slug: string; title: string; owner_name: string;
  cover_url: string; episode_count: number; updated_at: string; category: string | null;
}
// No `clean_txt` field by design: THAT column (the raw user-fed source corpus) is ALWAYS_PRIVATE, so directory.ts publishes no URL to it — don't re-add one. `text` is a different column: the chapter's own AI narration, already public as the audio. The writer decides which rows qualify (directory.ts gates on `audio.source='ai'` — a migrated/uploaded chapter's `text` IS that corpus); the reader just renders whatever it is handed.
// `text` is OPTIONAL — an artifact written before this shipped (or by the decommissioned OCDevel writer, cf. the `unlisted` shim below) has no such key, and every non-qualifying episode publishes null. Both must render no body and no orphan rule.
// `title` is NULLABLE, matching what directory.ts can actually emit (`entries.title` is a nullable varchar, and `audio.entry_id` is null for the whole upload→backfill window, so `withContent` folds to null). Declaring it `string` here put `null` into an `<h1>`, a `<title>` and a JSON-LD `name` with tsc seeing nothing — render through `episodeTitle`, never the raw field.
export interface ShowEpisode {
  id: string; series_number?: number | null; title: string | null; teaser: string | null; shownotes: string | null; text?: string | null;
  audio_url: string | null; final_duration_seconds: number | null; created_at: string;
}
export const episodeTitle = (e: ShowEpisode): string => e.title || 'Untitled episode';
// `series_number` is the SERVER's canonical chapter number (finished chapters in `(created_at, id)` order — directory.ts `chapterNumberSql`), absent for an episode that is not a story chapter. Formatting is the shared `formatChapterTitle`, honoring the show's "Hide chapter numbers" setting; never derive a number here.
export const numberedEpisodeTitle = (e: ShowEpisode, show: Pick<ShowDetail, 'hide_chapter_numbers'>): string =>
  formatChapterTitle(e.series_number, e.title || undefined, show.hide_chapter_numbers) || episodeTitle(e);
export interface ShowDetail {
  id: string; slug: string; title: string; description: string | null;
  user_id: string; image_alt: string | null; category: string | null;
  explicit: boolean; language: string | null; owner_name: string | null;
  subscribe_links: Array<{ platform: string; url: string }>;
  feed_url: string; cover_url: string;
  created_at: string; updated_at: string; privacy: string;
  /** The series' "Hide chapter numbers" setting. Absent from an artifact written before it shipped, which reads as shown. */
  hide_chapter_numbers?: boolean;
  /** `stories.mode === 'fiction'`: shownotes are spoilers, so the episode page collapses them. Absent on an older artifact, which reads as nonfiction until the next write. */
  fiction?: boolean;
  episodes: ShowEpisode[];
  /** Derived at read by `fetchShow`, never in the artifact: the flagship EPUB's URL, or null. */
  book_url?: string | null;
}
interface ListedShows {
  generated_at: string; shows: FeaturedShow[]; pages?: string[]; unlisted?: string[];
  bySlug: Record<string, ShowDetail>;
}

// Memoized: the prerender pass + every route loader share one Node process, so the artifact is
// fetched at most once per build. Evict on rejection so the first-build (no artifact yet, D1
// atom #5) failure doesn't wedge a permanently-rejected promise across callers.
let _artifact: Promise<ListedShows> | null = null;

// Tags every rejection of the artifact fetch itself (network, HTTP status, JSON parse), in place so callers still log the original error and its cause.
const ARTIFACT_FETCH = Symbol('artifactFetch');

// For the show/episode `loader`s, which run only at prerender (ssr:false). A genuine miss returns null without throwing. An artifact-fetch failure rethrows so the build fails: swallowing it would bake a 200 "not found" page for a show that exists (except on the ALLOW_EMPTY_SERIES bootstrap build, react-router.config.ts, whose placeholder pages have no artifact to read). Anything else is a data-shaping throw on one malformed show (e.g. `oldestFirst` on a bad episode): that page renders not-found with a warning, never failing the whole site build.
export function prerenderFetchFailed(what: string, e: unknown): null {
  if ((e as any)?.[ARTIFACT_FETCH] && process.env.ALLOW_EMPTY_SERIES !== '1') throw e;
  console.warn(`[series] ${what} failed; rendering not-found:`, e);
  return null;
}
function fetchListedShows(): Promise<ListedShows> {
  if (!_artifact) {
    _artifact = fetch(LISTED_SHOWS_URL).then(async (r) => {
      if (!r.ok) throw Object.assign(new Error(`HTTP ${r.status}`), { code: r.status });
      return (await r.json()) as ListedShows;
    }).catch((e) => { throw Object.assign(e instanceof Error ? e : new Error(String(e)), { [ARTIFACT_FETCH]: true }); });
    _artifact.catch(() => { _artifact = null; });
  }
  return _artifact;
}

// `limit` omitted ⇒ every featured show the artifact ships (the /series directory folds them client-side); a caller wanting a teaser passes one.
export async function fetchFeaturedShows(limit?: number): Promise<FeaturedShow[]> {
  const shows = (await fetchListedShows()).shows.filter((s) => s.slug);
  return limit == null ? shows : shows.slice(0, limit);
}

// Only the ONE episode on screen renders a body, but a loader's whole return is serialized into the
// prerendered page for hydration — so a `show` carrying every sibling's `text` inlines the show's
// entire narration corpus into all N+1 of its pages. Drop it from the list; `fetchEpisode` keeps the
// one it hands back separately.
const withoutBodies = (show: ShowDetail): ShowDetail =>
  ({ ...show, episodes: show.episodes.map(({ text: _body, ...e }) => e) });

// The show's episodes in reading order — the listing, episode route and book all read it. Audio time then id places episodes; numbered ones are then reordered by the server's `series_number` within their own slots (a retried chapter's audio is newer than a later chapter's). The NUMBER shown is always the server's, never this position.
export const oldestFirst = (show: ShowDetail): ShowDetail => {
  // A total order: audio time (then id) places every episode, then the numbered ones are reassigned in `series_number` order into the slots numbered episodes hold, so a retried chapter's newer audio cannot put 4 before 3 and the comparator stays transitive in mixed shows.
  const byTime = [...show.episodes].sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id));
  const numbered = byTime.filter((e) => e.series_number != null).sort((a, b) => a.series_number! - b.series_number! || a.id.localeCompare(b.id));
  let i = 0;
  return { ...show, episodes: byTime.map((e) => (e.series_number != null ? numbered[i++] : e)) };
};

// The owner's Gnothi user id. His featured shows are OCDevel's flagships: the SSG build ships each as an EPUB with an OCDevel copyright page (series-book.ts). Every other show's text belongs to its user, so it gets neither.
export const FLAGSHIP_OWNER_ID = '7408962c-51d6-4877-b65f-d9dac376b2f5';

// The chapters a flagship book carries: numbered episodes that publish a body. Empty for any other show, so it is also the one "does this show have a book" test, shared by the build that writes the file and the page that links it.
const isFlagship = (show: ShowDetail): boolean => show.privacy === 'featured' && show.user_id === FLAGSHIP_OWNER_ID;
export const bookEpisodes = (show: ShowDetail): ShowEpisode[] =>
  isFlagship(show) ? oldestFirst(show).episodes.filter((e) => e.text) : [];
export const bookUrl = (slug: string): string => `/series/${slug}.epub`;

export async function fetchShow(slug: string): Promise<ShowDetail | null> {
  const show = (await fetchListedShows()).bySlug[slug];
  return show ? { ...withoutBodies(oldestFirst(show)), book_url: bookEpisodes(show).length ? bookUrl(slug) : null } : null;
}

/** Every flagship show, bodies intact, for the SSG build's EPUB step. A cheap filter only: `bookEpisodes` can throw on a malformed episode, so the build calls it per show inside its own catch. */
export async function fetchBookShows(): Promise<ShowDetail[]> {
  return Object.values((await fetchListedShows()).bySlug).filter((s) => s.slug && isFlagship(s));
}

// Reads the RAW artifact, not `fetchShow`: the episode handed back must keep its `text`, which `fetchShow` now strips.
export async function fetchEpisode(slug: string, id: string): Promise<{ show: ShowDetail; episode: ShowEpisode } | null> {
  const raw = (await fetchListedShows()).bySlug[slug] ?? null;
  const show = raw ? oldestFirst(raw) : null;
  const episode = show?.episodes.find((e) => String(e.id) === String(id)) ?? null;
  return show && episode ? { show: withoutBodies(show), episode } : null;
}

/** seconds → "m:ss" / "h:mm:ss" for episode-list + episode-detail display. */
export function formatDuration(secs: number | null): string | null {
  if (!secs) return null;
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = Math.floor(secs % 60);
  const mm = h ? String(m).padStart(2, '0') : String(m);
  return `${h ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** ISO timestamp → "12 Mar 2026", UTC. Hand-rolled rather than `toLocaleDateString`: this renders at PRERENDER (Node, whatever locale/TZ the builder has) and again at hydrate (the visitor's), and any disagreement is a React hydration mismatch on a static page. */
export function formatUpdated(iso: string | null): string | null {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

export interface PrerenderShow {
  slug: string; featured: boolean;
  /** Real artifact dates, carried solely so the sitemap can emit a truthful `<lastmod>` — never substitute build time for a missing one. */
  updated_at: string | null;
  episodes: { id: string; created_at: string | null }[];
}

/** Every slug (featured + pages) that needs a prerendered show/episode page, with the episodes react-router.config.ts needs to enumerate `/series/:slug/:episodeid` paths, and whether it's featured (sitemap-eligible) vs plain `pages` (prerendered + noindex, but not sitemap-listed). */
export async function fetchPrerenderShows(): Promise<PrerenderShow[]> {
  const data = await fetchListedShows();
  // `podcasts.slug` is nullable and directory.ts slug-filters `bySlug`/`pages` but NOT `shows`, so a slugless featured row would prerender `/series/null` and put that URL in the sitemap.
  const featuredSlugs = new Set(data.shows.filter((s) => s.slug).map((s) => s.slug));
  // READ-SIDE SHIM: the deployed writer (`directory.ts`) emits `pages`, but the artifact a build READS may still be the last one the decommissioned OCDevel writer left in the bucket, which emitted `unlisted` — without this rung that build takes the `?? []` and silently ships zero pages for that tier, no error. Delete once a post-cutover regen has written a `pages` artifact. No `ssg` rung: no writer in either tree ever emitted that key.
  const slugs = [...new Set([...featuredSlugs, ...(data.pages ?? data.unlisted ?? [])])];
  return slugs.map((slug) => {
    const show = data.bySlug[slug];
    return {
      slug, featured: featuredSlugs.has(slug),
      updated_at: show?.updated_at ?? null,
      episodes: (show?.episodes ?? []).map((e) => ({ id: String(e.id), created_at: e.created_at ?? null })),
    };
  });
}
